import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';

interface MaintenanceModeAccessProps {
  onSuccess?: (sessionId: string, machineId: string, employeeId: string) => void;
  onCancel?: () => void;
}

interface MaintenanceModeAccessResponse {
  success: boolean;
  machineId?: string;
  employeeId?: string;
  sessionId?: string;
  message?: string;
}

export const MaintenanceModeAccess: React.FC<MaintenanceModeAccessProps> = ({
  onSuccess,
  onCancel,
}) => {
  const navigate = useNavigate();
  const [machineCode, setMachineCode] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // Validation
    if (!machineCode.trim()) {
      setError('Por favor, insira o código da máquina');
      return;
    }

    if (!employeeCode.trim()) {
      setError('Por favor, insira o código do funcionário');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post<MaintenanceModeAccessResponse>(
        '/maintenance-sessions/access',
        {
          machineCode: machineCode.trim(),
          employeeCode: employeeCode.trim(),
        }
      );

      if (response.data.success && response.data.sessionId && response.data.machineId && response.data.employeeId) {
        setSuccessMessage('Acesso concedido! Redirecionando...');
        
        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess(response.data.sessionId, response.data.machineId, response.data.employeeId);
        } else {
          // Navigate to maintenance session page
          setTimeout(() => {
            navigate(`/maintenance-session/${response.data.sessionId}`);
          }, 1000);
        }
      } else {
        setError(response.data.message || 'Falha ao acessar modo de manutenção');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Erro ao solicitar acesso';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setMachineCode('');
    setEmployeeCode('');
    setError(null);
    setSuccessMessage(null);
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Acesso ao Modo de Manutenção
          </h2>
          <p className="text-sm text-gray-600">
            Insira o código da máquina e seu código de funcionário para iniciar a manutenção
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Machine Code Input */}
          <div>
            <label htmlFor="machineCode" className="block text-sm font-medium text-gray-700 mb-2">
              Código da Máquina
            </label>
            <input
              id="machineCode"
              type="text"
              value={machineCode}
              onChange={(e) => setMachineCode(e.target.value.toUpperCase())}
              placeholder="Ex: MACH001"
              disabled={isLoading}
              className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed font-mono text-lg"
              autoComplete="off"
            />
          </div>

          {/* Employee Code Input */}
          <div>
            <label htmlFor="employeeCode" className="block text-sm font-medium text-gray-700 mb-2">
              Código do Funcionário
            </label>
            <input
              id="employeeCode"
              type="text"
              value={employeeCode}
              onChange={(e) => setEmployeeCode(e.target.value.toUpperCase())}
              placeholder="Ex: EMP7K2Q9"
              disabled={isLoading}
              className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed font-mono text-lg"
              autoComplete="off"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-green-800">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isLoading || !machineCode.trim() || !employeeCode.trim()}
              className="flex-1 inline-flex items-center justify-center px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verificando...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                  Solicitar Acesso
                </>
              )}
            </button>

            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={isLoading}
                className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancelar
              </button>
            )}

            {!onCancel && (machineCode || employeeCode) && (
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Limpar
              </button>
            )}
          </div>
        </form>

        {/* Help Text */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            <strong>Nota:</strong> Ambos os códigos devem ser válidos para acessar o modo de manutenção. 
            Se você não possui um código de funcionário, entre em contato com o administrador.
          </p>
        </div>
      </div>
    </div>
  );
};
